import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type MaintenanceResponse = 'yes' | 'no' | 'needs_repair';
export type MaintenanceReportStatus = 'open' | 'in_progress' | 'claiming' | 'resolved';

export interface MaintenanceReport {
  id: string;
  property_id: string;
  cleaning_task_id: string | null;
  task_description: string;
  room_name: string;
  checklist_name: string | null;
  cleaner_id: string;
  response: MaintenanceResponse;
  notes: string | null;
  photos: string[];
  created_at: string;
  status: MaintenanceReportStatus;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  unit_item_id?: string | null;
  cleaners?: {
    id: string;
    name: string;
  };
}

export interface CreateMaintenanceReportInput {
  property_id: string;
  cleaning_task_id?: string;
  task_description: string;
  room_name: string;
  checklist_name?: string;
  cleaner_id: string;
  response: MaintenanceResponse;
  notes?: string;
  photos?: string[];
  /** Slice 58 — the unit item this flag is about, so it lands on that item's row in the report. */
  unit_item_id?: string | null;
}

export const useMaintenanceReports = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["maintenance-reports", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      
      const { data: reports, error } = await supabase
        .from("maintenance_issue_reports")
        .select("*")
        .eq("property_id", propertyId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      if (!reports || reports.length === 0) return [];
      
      // Get unique cleaner IDs
      const cleanerIds = [...new Set(reports.map((r) => r.cleaner_id))];
      
      // Fetch cleaner names
      const { data: cleaners } = await supabase
        .from("cleaners")
        .select("id, name")
        .in("id", cleanerIds);
      
      const cleanerMap = new Map(cleaners?.map((c) => [c.id, c]) || []);
      
      return reports.map((report) => ({
        ...report,
        status: (report.status || 'open') as MaintenanceReportStatus,
        cleaners: cleanerMap.get(report.cleaner_id) || undefined,
      })) as MaintenanceReport[];
    },
    enabled: !!propertyId,
    staleTime: 15_000, // Phones and other accounts change this data; recheck soon
    refetchOnMount: true,
    placeholderData: [], // Show empty array while loading
  });
};

export const useCreateMaintenanceReport = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: CreateMaintenanceReportInput) => {
      const { data, error } = await supabase
        .from("maintenance_issue_reports")
        .insert({
          property_id: input.property_id,
          cleaning_task_id: input.cleaning_task_id || null,
          task_description: input.task_description,
          room_name: input.room_name,
          checklist_name: input.checklist_name || null,
          cleaner_id: input.cleaner_id,
          response: input.response,
          notes: input.notes || null,
          photos: input.photos || [],
          status: 'open',
          ...(input.unit_item_id ? { unit_item_id: input.unit_item_id } : {}),
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Create owner notification if response is "no" or "needs_repair"
      if (input.response === 'no' || input.response === 'needs_repair') {
        try {
          // Get property details for notification
          const { data: property } = await supabase
            .from("properties")
            .select("owner_id, name")
            .eq("id", input.property_id)
            .single();
          
          if (property) {
            const issueType = input.response === 'no' ? 'not working' : 'needs repair';
            const title = input.response === 'no' 
              ? `🔴 Maintenance Issue: ${input.task_description.substring(0, 30)}`
              : `🟡 Repair Needed: ${input.task_description.substring(0, 30)}`;
            
            const message = `A maintenance check at ${property.name} reported "${input.task_description}" as ${issueType}.${input.notes ? ` Notes: "${input.notes.substring(0, 100)}"` : ''}`;
            
            await supabase
              .from("owner_notifications")
              .insert({
                owner_id: property.owner_id,
                property_id: input.property_id,
                cleaning_task_id: input.cleaning_task_id || null,
                type: 'maintenance_issue',
                title,
                message,
              });
          }
        } catch (notifError) {
          console.error("Failed to create notification:", notifError);
          // Don't fail the main operation if notification fails
        }
      }
      
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-reports", variables.property_id] });
    },
  });
};

export const useUpdateMaintenanceReportStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      reportId,
      status,
      resolutionNote,
      resolvedBy,
      propertyId,
    }: {
      reportId: string;
      status: MaintenanceReportStatus;
      resolutionNote?: string;
      resolvedBy?: string;
      propertyId: string;
    }) => {
      const updateData: Record<string, unknown> = { status };
      
      if (status === "resolved") {
        updateData.resolved_at = new Date().toISOString();
        updateData.resolution_note = resolutionNote || null;
        updateData.resolved_by = resolvedBy || null;
      } else {
        updateData.resolved_at = null;
        updateData.resolution_note = null;
        updateData.resolved_by = null;
      }
      
      const { data, error } = await supabase
        .from("maintenance_issue_reports")
        .update(updateData)
        .eq("id", reportId)
        .select()
        .single();
      
      if (error) throw error;
      return { data, propertyId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-reports", result.propertyId] });
    },
  });
};

export const useUploadMaintenancePhoto = () => {
  return useMutation({
    mutationFn: async ({ 
      propertyId, 
      cleaningTaskId, 
      roomName,
      taskDescription,
      file 
    }: { 
      propertyId: string;
      cleaningTaskId: string;
      roomName: string;
      taskDescription: string;
      file: File;
    }) => {
      const timestamp = Date.now();
      const sanitizedRoom = roomName.replace(/[^a-zA-Z0-9]/g, '_');
      const sanitizedTask = taskDescription.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${propertyId}/${cleaningTaskId}/maintenance/${sanitizedRoom}/${sanitizedTask}_${timestamp}.jpg`;
      
      const { data, error } = await supabase.storage
        .from("cleaning-photos")
        .upload(fileName, file, {
          contentType: file.type,
          upsert: true,
        });
      
      if (error) throw error;
      
      const { data: publicUrl } = supabase.storage
        .from("cleaning-photos")
        .getPublicUrl(data.path);
      
      return publicUrl.publicUrl;
    },
  });
};
